import { React, useState, useEffect } from "react";
import { api } from "../utils/api"; // Import your api function
import { ErrorMessage } from "formik";
import ModalPopup from "./ModalPopup";
import { TextField, Button, CircularProgress } from "@mui/material";
import { Formik, Form, Field, ErrorMessage as FormikErrorMessage } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";

export const DATA_GRID_STYLES = "box-content shadow-sm grid grid-cols-2 divide-y border [&>span]:p-4";

const resolveUserTypeFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const url = window.location.href.toLowerCase();

  if (url.includes("customer")) {
    return "customer";
  }

  if (url.includes("partner") && url.includes("user")) {
    return "partner";
  }

  if (url.includes("merchant") && url.includes("user")) {
    return "merchant";
  }

  if (url.includes("administrator")) {
    return "administrator";
  }

  return null;
};

const BasicUserInfo = ({ user, hideResendWelcomeEmail, merchantId }) => {
  const params = useParams();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [resendingWelcomeEmail, setResendingWelcomeEmail] = useState(false);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  
  // Debug: Log merchant ID sources when component mounts or merchantId changes
  useEffect(() => {
    if (resolveUserTypeFromUrl() === "merchant") {
      console.log("BasicUserInfo - Merchant ID sources:", {
        merchantIdProp: merchantId,
        paramsIdMerchant: params?.idMerchant,
        paramsId: params?.id,
        userMerchantAccount: user?.merchantAccount,
        url: window.location.pathname
      });
    }
  }, [merchantId, params, user]);

  const ErrorMessage = () =>
    error && (
      <div className="fixed bottom-0 right-0">
        <div
          id="basicUserInfo-alert-error"
          className="flex items-center p-2 mb-2 text-red-800 border-t-4 border-red-300 bg-red-50 dark:text-red-400 dark:bg-gray-800 dark:border-red-800"
          role="alert"
        >
          <div className="ms-3 text-sm font-medium">{error}</div>
        </div>
      </div>
    );
  const SuccessMessage = () =>
    success && (
      <div className="fixed bottom-0 right-0">
        <div
          id="basicUserInfo-alert-success"
          className="flex items-center p-2 mb-2 text-blue-800 border-t-4 border-blue-300 bg-blue-50 dark:text-blue-400 dark:bg-gray-800 dark:border-blue-800"
          role="alert"
        >
          <div className="ms-3 text-sm font-medium">{success}</div>
        </div>
      </div>
    );

  const resendWelcomeEmail = async () => {
    if (resendingWelcomeEmail) {
      return;
    }

    const userType = resolveUserTypeFromUrl();

    let endpoint = null;
    const dataToSend = {
      idUser: user._id
    };
    switch (userType) {
      case "customer":
        endpoint = `v1/customer/resendInvitation`;
        break;

      case "partner":
        endpoint = `v1/user/sendWelcomeEmail`; // Corrected endpoint
        break;

      case "merchant":
        endpoint = `v1/merchantUser/sendWelcomeEmail`; // Corrected endpoint
        break;

      case "administrator":
        endpoint = `v1/administrator/sendWelcomeEmail`; // Corrected endpoint
        break;

      default:
        break;
    }

    setError("");
    setSuccess("");
    setResendingWelcomeEmail(true);
    try {
      if (userType && endpoint) {
        const response = await api(endpoint, "POST", dataToSend); // Assuming no data to send
        if (response.data.success) {
          setSuccess(response.data.message);
        } else {
          setError(response.data.message);
        }
      } else {
        setError("User type or Endpoint can not be resolved.");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to send welcome email."
      );
    } finally {
      setResendingWelcomeEmail(false);
    }
  };

  const sendPasswordResetEmail = async () => {
    const userType = resolveUserTypeFromUrl();

    if (userType !== "customer" && userType !== "merchant") {
      setError("Password reset email is only available for customers and merchant users.");
      return;
    }

    if (!user?._id || sendingPasswordReset) {
      return;
    }

    setError("");
    setSuccess("");
    setSendingPasswordReset(true);
    try {
      let endpoint;
      
      if (userType === "customer") {
        endpoint = `v1/customer/${user._id}/send-reset-password`;
      } else if (userType === "merchant") {
        // Get merchant ID from multiple sources (same logic as set password)
        let merchantIdToUse = null;
        
        // First, try to get from user object (most reliable source)
        if (user?.merchantAccount) {
          if (typeof user.merchantAccount === 'object') {
            merchantIdToUse = user.merchantAccount._id || user.merchantAccount.toString();
          } else {
            merchantIdToUse = String(user.merchantAccount);
          }
        }
        
        // Fallback to prop
        if (!merchantIdToUse) {
          merchantIdToUse = merchantId;
        }
        
        // Fallback to route params
        if (!merchantIdToUse) {
          merchantIdToUse = params?.idMerchant || params?.id;
        }
        
        // Final fallback to URL parsing
        if (!merchantIdToUse) {
          const urlParts = window.location.pathname.split("/").filter(part => part);
          const merchantIndex = urlParts.findIndex(part => part === "merchant");
          if (merchantIndex !== -1 && urlParts[merchantIndex + 1]) {
            merchantIdToUse = urlParts[merchantIndex + 1];
          }
        }
        
        if (!merchantIdToUse) {
          console.error("Merchant ID resolution failed for password reset. Debug info:", { 
            merchantIdProp: merchantId, 
            params, 
            userMerchantAccount: user?.merchantAccount,
            userObject: user,
            url: window.location.pathname 
          });
          setError("Unable to determine merchant account ID. Please refresh the page and try again.");
          setSendingPasswordReset(false);
          return;
        }
        
        endpoint = `v1/merchant/${merchantIdToUse}/user/${user._id}/send-reset-password`;
      }

      const response = await api(
        endpoint,
        "POST",
        null
      );

      if (response?.data?.success) {
        setSuccess(response.data.message || "Password reset email sent successfully.");
      } else {
        setError(response?.data?.message || "Failed to send password reset email.");
      }
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setError(error.response?.data?.message || "Unexpected error while sending password reset email.");
    }
    setSendingPasswordReset(false);
  };

  const setCustomerPassword = async (password) => {
    const userType = resolveUserTypeFromUrl();

    if (userType !== "customer" && userType !== "merchant") {
      setError("Password setting is only available for customers and merchant users.");
      return;
    }

    if (!user?._id || settingPassword) {
      return;
    }

    setError("");
    setSuccess("");
    setSettingPassword(true);
    try {
      let endpoint;
      let successMessage;
      
      if (userType === "customer") {
        endpoint = `v1/customer/${user._id}/set-password`;
        successMessage = "Customer password set successfully.";
      } else if (userType === "merchant") {
        // Get merchant ID from multiple sources (in order of preference):
        // 1. User object's merchantAccount field (most reliable)
        // 2. Prop passed from parent component
        // 3. Route params
        // 4. URL parsing
        let merchantIdToUse = null;
        
        // First, try to get from user object (most reliable source)
        if (user?.merchantAccount) {
          if (typeof user.merchantAccount === 'object') {
            merchantIdToUse = user.merchantAccount._id || user.merchantAccount.toString();
          } else {
            merchantIdToUse = String(user.merchantAccount);
          }
        }
        
        // Fallback to prop
        if (!merchantIdToUse) {
          merchantIdToUse = merchantId;
        }
        
        // Fallback to route params
        if (!merchantIdToUse) {
          merchantIdToUse = params?.idMerchant || params?.id;
        }
        
        // Final fallback to URL parsing
        if (!merchantIdToUse) {
          const urlParts = window.location.pathname.split("/").filter(part => part);
          const merchantIndex = urlParts.findIndex(part => part === "merchant");
          if (merchantIndex !== -1 && urlParts[merchantIndex + 1]) {
            merchantIdToUse = urlParts[merchantIndex + 1];
          }
        }
        
        if (!merchantIdToUse) {
          console.error("Merchant ID resolution failed. Debug info:", { 
            merchantIdProp: merchantId, 
            params, 
            userMerchantAccount: user?.merchantAccount,
            userObject: user,
            url: window.location.pathname 
          });
          setError("Unable to determine merchant account ID. Please refresh the page and try again.");
          setSettingPassword(false);
          return;
        }
        
        endpoint = `v1/merchant/${merchantIdToUse}/user/${user._id}/set-password`;
        successMessage = "Merchant user password set successfully.";
      }

      const response = await api(
        endpoint,
        "POST",
        { password }
      );

      if (response?.data?.success) {
        setSuccess(response.data.message || successMessage);
        setShowSetPasswordModal(false);
      } else {
        setError(response?.data?.message || `Failed to set ${userType === "customer" ? "customer" : "merchant user"} password.`);
      }
    } catch (error) {
      console.error(`Error setting ${userType === "customer" ? "customer" : "merchant user"} password:`, error);
      setError(error.response?.data?.message || `Unexpected error while setting ${userType === "customer" ? "customer" : "merchant user"} password.`);
    }
    setSettingPassword(false);
  };

  const passwordValidationSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Please confirm your password")
  });

  const currentUserType = resolveUserTypeFromUrl();
  const shouldShowPasswordReset =
    !hideResendWelcomeEmail && (currentUserType === "customer" || currentUserType === "merchant");
  const shouldShowSetPassword =
    !hideResendWelcomeEmail && 
    (currentUserType === "customer" || currentUserType === "merchant") && 
    sessionStorage.getItem("selectedPortal") === "Administrator";

  return (
    <div className={DATA_GRID_STYLES}>
      <span className="font-semibold border-none">Email</span>
      <span className="font-semibold border-none">
        <div className="flex flex-col gap-2 items-start">
          <a href={"mailto:" + user.email}>{user.email}</a>
          {!hideResendWelcomeEmail && (
            <button
              type="button"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 min-w-[200px]"
              onClick={resendWelcomeEmail}
              disabled={resendingWelcomeEmail}
              aria-busy={resendingWelcomeEmail}
              aria-label={
                resendingWelcomeEmail
                  ? "Sending welcome email, please wait"
                  : "Resend welcome email"
              }
            >
              {resendingWelcomeEmail && (
                <CircularProgress size={18} sx={{ color: "white" }} />
              )}
              {resendingWelcomeEmail ? "Sending…" : "Resend Welcome Email"}
            </button>
          )}
        </div>
      </span>
      <span>Full Name</span>
      <span>
        {user.firstName} {user.lastName}
      </span>
      <span className="font-semibold">Phone</span>
      <span className="font-semibold">
        {user.phone ? <a href={"tel:" + user.phone}>{user.phone}</a> : "No phone available"}
      </span>
      <span>STREET</span>
      <span>{user?.address?.street || "-"}</span>
      <span>CITY</span>
      <span>{user?.address?.city || ""}</span>
      <span>STATE</span>
      <span>{user?.address?.state || ""}</span>
      <span>POSTCODE</span>
      <span>{user?.address?.postcode || ""}</span>
      <span>COUNTRY</span>
      <span>{user?.address?.country?.value || user?.address?.country || ""}</span>
      {user?.vehicles?.length > 0 && (
        <>
          <span className="font-semibold">Vehicles</span>
          <span className="flex flex-column box-content">
            {user.vehicles.map((vehicle) => (
              <a href={"/vehicle/" + vehicle?._id}>{vehicle?.rego}</a>
            ))}
          </span>
        </>
      )}
      {user?.role && (
        <>
          <span>Role</span>
          <span>{user.role}</span>
        </>
      )}
      <span className="font-semibold">Status</span>
      <span className="flex flex-column box-content">{user.active ? "Active" : "Not Active"}</span>
      {(shouldShowPasswordReset || shouldShowSetPassword) && (
        <>
          <span className="font-semibold">Password Actions</span>
          <span className="flex flex-col gap-2">
            {shouldShowPasswordReset && (
              <button
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={sendPasswordResetEmail}
                disabled={sendingPasswordReset}
              >
                {sendingPasswordReset ? "Sending Reset Email..." : "Send Password Reset"}
              </button>
            )}
            {shouldShowSetPassword && (
              <button
                className="bg-purple-500 hover:bg-purple-600 text-white font-bold p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setShowSetPasswordModal(true)}
              >
                Set Password Manually
              </button>
            )}
          </span>
        </>
      )}
      <div className="flex absolute">
        <SuccessMessage />
        <ErrorMessage />
      </div>

      {/* Set Password Modal */}
      <ModalPopup
        showModal={showSetPasswordModal}
        handleShowModal={() => setShowSetPasswordModal(false)}
        popupTitle={currentUserType === "merchant" ? "Set Merchant User Password" : "Set Customer Password"}
        popupContent={
          <Formik
            initialValues={{
              password: "",
              confirmPassword: ""
            }}
            validationSchema={passwordValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setCustomerPassword(values.password);
              setSubmitting(false);
            }}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form className="flex flex-col gap-4 p-4">
                <div>
                  <Field
                    as={TextField}
                    name="password"
                    type="password"
                    label="New Password"
                    fullWidth
                    variant="outlined"
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                    disabled={settingPassword || isSubmitting}
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    name="confirmPassword"
                    type="password"
                    label="Confirm Password"
                    fullWidth
                    variant="outlined"
                    error={touched.confirmPassword && !!errors.confirmPassword}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    disabled={settingPassword || isSubmitting}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outlined"
                    onClick={() => setShowSetPasswordModal(false)}
                    disabled={settingPassword || isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={settingPassword || isSubmitting}
                  >
                    {settingPassword ? "Setting Password..." : "Set Password"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        }
        cancelButtonText=""
        confirmButtonText=""
        confirmCallbackAction=""
      />
    </div>
  );
};

export default BasicUserInfo;
